/* ═══════════════════════════════════════════════════════════════
   Spec Library — Spec Master catalog with lock/draft indicators
   Shows published spec masters, lock status, and pending PRs
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { DataTable, Column, GroupOption } from '../components/DataTable';
import { Button } from '../components/ui/button';
import EmptyState from '../components/EmptyState';
import { SpecService, diffBlocks, diffChangeSummary, ChangeSummary } from '../services/specService';
import { pushEditHistory } from '../../shared/editHistory';
import { hasPermission } from '../../shared/permissions';
import { isWeb } from '../lib/platform';
import type { SpecMasterDraft, BlockDiff } from '../../shared/types';

interface SpecLibraryProps {
  onOpenEditor: (id: string) => void;
  toast: (msg: string, isError?: boolean) => void;
  userRole?: 'site_admin' | 'admin' | 'manager' | 'member';
  userName?: string;
  /** Granular permission keys from AuthUser.permissions. Falls back to userRole if omitted. */
  userPermissions?: string[];
}

/** Conflict resolution for imported spec that matches an existing master */
interface ImportConflict {
  /** The extracted data from the docx */
  csiNumber: string;
  title: string;
  blocks: any[];
  division: string;
  /** The existing master that conflicts */
  existingMaster: any;
}

// ─── Token-derived soft tints (no raw hex — see docs/DESIGN.md §2) ───
const WARN_TEXT = 'color-mix(in srgb, var(--color-warning) 60%, var(--tx))';
const WARN_SOFT = 'color-mix(in srgb, var(--color-warning) 12%, transparent)';
const WARN_SOFT_HOVER = 'color-mix(in srgb, var(--color-warning) 18%, transparent)';
const WARN_BORDER = 'color-mix(in srgb, var(--color-warning) 40%, transparent)';
const DANGER_SOFT = 'color-mix(in srgb, var(--dg) 8%, transparent)';
const OK_SOFT = 'rgba(var(--okr), 0.08)';

// ─── Inline Lucide-style icons (no emoji as icons — rule no-emoji-icons) ───
function SvgIcon({ size = 14, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}
const LockIcon = ({ size = 12 }: { size?: number }) => (
  <SvgIcon size={size}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </SvgIcon>
);
const UnlockIcon = ({ size = 14 }: { size?: number }) => (
  <SvgIcon size={size}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </SvgIcon>
);
const TrashIcon = ({ size = 14 }: { size?: number }) => (
  <SvgIcon size={size}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </SvgIcon>
);
const CopyIcon = ({ size = 14 }: { size?: number }) => (
  <SvgIcon size={size}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </SvgIcon>
);
const PublishIcon = ({ size = 14 }: { size?: number }) => (
  <SvgIcon size={size}>
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </SvgIcon>
);
const CheckIcon = ({ size = 12 }: { size?: number }) => (
  <SvgIcon size={size}>
    <polyline points="20 6 9 17 4 12" />
  </SvgIcon>
);
const PlusIcon = ({ size = 14 }: { size?: number }) => (
  <SvgIcon size={size}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </SvgIcon>
);
const ChevronRightIcon = ({ size = 12 }: { size?: number }) => (
  <SvgIcon size={size}>
    <polyline points="9 18 15 12 9 6" />
  </SvgIcon>
);
const AlertIcon = ({ size = 14 }: { size?: number }) => (
  <SvgIcon size={size}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </SvgIcon>
);
const DownloadIcon = ({ size = 14 }: { size?: number }) => (
  <SvgIcon size={size}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </SvgIcon>
);
const BookIcon = ({ size = 28 }: { size?: number }) => (
  <SvgIcon size={size}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </SvgIcon>
);

/** Skeleton shown during the first data load (skeletons over spinners — DESIGN.md §4). */
function LibrarySkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading spec masters"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
    >
      <div
        style={{
          height: 36,
          maxWidth: 320,
          background: 'var(--sa)',
          borderRadius: 'var(--radius-md)',
          animation: 'pulse 1.4s ease-in-out infinite',
        }}
      />
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 40,
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            opacity: Math.max(0.25, 0.95 - i * 0.1),
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

function SpecLibrary({
  onOpenEditor,
  toast,
  userRole = 'admin',
  userName = 'user',
  userPermissions,
}: SpecLibraryProps) {
  // Permission-first. Every gate reads from userPermissions; no role
  // fallback. Custom firm roles compose their exact permission set in
  // the admin center, so a role-based fallback can't express them.
  const permCan = (key: string) => hasPermission(userPermissions, key);
  const [masters, setMasters] = React.useState<any[]>([]);
  const [drafts, setDrafts] = React.useState<SpecMasterDraft[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  // Search is handled by DataTable's built-in search bar
  const [sortBy, setSortBy] = React.useState('csi');
  // View mode (table/tile) is owned by DataTable now — defaults to table per Josh's rule.
  const [selectedDiv, setSelectedDiv] = React.useState<string | null>(null);
  const [expandedDivs, setExpandedDivs] = React.useState<any>({});
  const [showNewModal, setShowNewModal] = React.useState(false);
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [reviewDraft, setReviewDraft] = React.useState<SpecMasterDraft | null>(null);
  const [reviewDiff, setReviewDiff] = React.useState<BlockDiff[]>([]);
  const [reviewChangeSummary, setReviewChangeSummary] = React.useState<ChangeSummary | null>(null);
  const [reviewNote, setReviewNote] = React.useState('');
  const [newCsi, setNewCsi] = React.useState('');
  const [newTitle, setNewTitle] = React.useState('');

  // Import state
  const [importing, setImporting] = React.useState(false);
  const [importStatus, setImportStatus] = React.useState('');
  const [importConflict, setImportConflict] = React.useState<ImportConflict | null>(null);
  const [conflictNewCsi, setConflictNewCsi] = React.useState('');
  const [conflictNewTitle, setConflictNewTitle] = React.useState('');

  const canCreate = permCan('specs.create');
  // "Can edit" at the library level means either owning a draft or being
  // able to take over anyone's draft. Library-level lets us show the Edit
  // button; the editor itself further gates individual edit actions.
  const canEdit = permCan('specs.edit_own') || permCan('specs.edit_any');
  const canReview = permCan('specs.approve_review');
  const canDelete = permCan('specs.delete');
  const canPublish = permCan('specs.publish');
  const canArchive = permCan('specs.archive');
  const canOverrideDraft = permCan('specs.override_draft');
  const canImport = permCan('specs.import');

  // Bulk mode + multi-select state
  const [bulkMode, setBulkMode] = React.useState(false);
  const [selectedSpecIds, setSelectedSpecIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    loadData();
  }, []);
  React.useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  async function loadData() {
    // Promise.allSettled, never Promise.all — data never blank (DESIGN.md §5).
    const [listRes, draftRes] = await Promise.allSettled([
      SpecService.listMasters(),
      SpecService.listDrafts(),
    ]);
    if (listRes.status === 'fulfilled') setMasters(listRes.value);
    if (draftRes.status === 'fulfilled') setDrafts(draftRes.value);
    if (listRes.status === 'rejected' || draftRes.status === 'rejected') {
      setLoadError(
        "Couldn't load the spec library. Check your connection — the list refreshes automatically when sync completes."
      );
    } else {
      setLoadError(null);
    }
    setLoading(false);
  }

  // Pending PRs for admin review
  const pendingPRs = React.useMemo(
    () => drafts.filter((d) => d.publishRequest?.status === 'pending'),
    [drafts]
  );

  const divisions = SpecService.getDivisions();
  const filtered = React.useMemo(() => {
    let result = [...masters];
    if (selectedDiv) result = result.filter((m: any) => m.division === selectedDiv);
    // Search is now handled by DataTable's built-in search — no duplicate filtering here
    result.sort((a: any, b: any) => {
      if (sortBy === 'csi') return a.csiNumber.localeCompare(b.csiNumber);
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      if (sortBy === 'modified') return (b.lastModified || '').localeCompare(a.lastModified || '');
      return 0;
    });
    return result;
  }, [masters, selectedDiv, sortBy]);

  const divCounts = React.useMemo(() => {
    const counts: any = {};
    masters.forEach((m: any) => {
      counts[m.division] = (counts[m.division] || 0) + 1;
    });
    return counts;
  }, [masters]);

  // ─── CSI uniqueness: one Master Spec per CSI number (case/whitespace-insensitive).
  // Variants live INSIDE a master; two masters can never share a number. ───
  function normalizeCsi(s: string) {
    return (s || '').trim().toLowerCase();
  }
  function csiExists(csi: string, ignoreId?: string): any | null {
    const k = normalizeCsi(csi);
    if (!k) return null;
    return masters.find((m: any) => normalizeCsi(m.csiNumber) === k && m.id !== ignoreId) || null;
  }

  async function handleNew() {
    if (!newCsi.trim()) {
      toast('Enter a CSI number', true);
      return;
    }
    const clash = csiExists(newCsi);
    if (clash) {
      toast(
        `CSI ${newCsi.trim()} already exists as "${clash.title}". Use a different number or edit that master.`,
        true
      );
      return;
    }
    const master = {
      id: SpecService.generateId(),
      csiNumber: newCsi.trim(),
      title: newTitle.trim() || 'Untitled Specification',
      division: newCsi.trim().substring(0, 2),
      status: 'draft',
      version: 1,
      lastModified: new Date().toISOString(),
      modifiedBy: userName,
      coreContent: [
        { id: SpecService.generateId(), level: 1, text: 'GENERAL' },
        { id: SpecService.generateId(), level: 2, text: 'SUMMARY' },
        { id: SpecService.generateId(), level: 3, text: 'Section includes...' },
        { id: SpecService.generateId(), level: 1, text: 'PRODUCTS' },
        { id: SpecService.generateId(), level: 1, text: 'EXECUTION' },
      ],
      systems: [] as any[],
      editHistory: pushEditHistory([], userName, undefined, 'created'),
    };
    await SpecService.savePublished(master as any);
    setShowNewModal(false);
    setNewCsi('');
    setNewTitle('');
    await loadData();
    onOpenEditor(master.id);
  }

  async function handleDuplicate(e: React.MouseEvent, m: any) {
    e.stopPropagation();
    // Force a new CSI number up-front so the duplicate can never collide with
    // the source or another existing master.
    const suggested = (m.csiNumber || '') + '-COPY';
    const entered = prompt(
      `Duplicate "${m.csiNumber} ${m.title}"\n\nEnter a NEW CSI number for the copy (current: ${m.csiNumber}). Variants belong INSIDE a master — two masters cannot share a CSI number.`,
      suggested
    );
    if (!entered || !entered.trim()) {
      toast('Duplicate cancelled — a unique CSI number is required', true);
      return;
    }
    if (normalizeCsi(entered) === normalizeCsi(m.csiNumber)) {
      toast('New CSI number must differ from the original', true);
      return;
    }
    const clash = csiExists(entered);
    if (clash) {
      toast(`CSI ${entered.trim()} already exists as "${clash.title}"`, true);
      return;
    }
    const dup = await SpecService.saveAsNewMaster(
      { ...m, csiNumber: entered.trim() },
      m.title + ' (Copy)',
      userName
    );
    await loadData();
    toast(`Duplicated as ${entered.trim()}`);
  }

  async function handleOverrideDraft(e: React.MouseEvent, m: any) {
    e.stopPropagation();
    if (!m.lockedBy) return;
    if (
      !confirm(
        `Override ${m.lockedBy}'s draft for "${m.csiNumber} - ${m.title}"? Their unsaved changes will be discarded.`
      )
    )
      return;
    await SpecService.forceOverrideDraft(m.id);
    await loadData();
    toast(`Draft overridden — ${m.csiNumber} unlocked`);
  }

  async function handleDelete(e: React.MouseEvent, m: any) {
    e.stopPropagation();
    if (!canDelete) {
      toast('You do not have permission to delete specs', true);
      return;
    }
    if (m.lockedBy) {
      toast(`Cannot delete — locked by ${m.lockedBy}`, true);
      return;
    }
    if (!confirm('Delete "' + m.csiNumber + ' - ' + m.title + '"?')) return;
    await SpecService.deleteMaster(m.id);
    await loadData();
    toast('Deleted');
  }

  async function handleMultiDelete() {
    if (!canDelete) {
      toast('You do not have permission to delete specs', true);
      return;
    }
    if (selectedSpecIds.size === 0) return;
    const selected = masters.filter((m) => selectedSpecIds.has(m.id));
    const locked = selected.filter((m) => m.lockedBy);
    if (locked.length > 0) {
      toast(
        `Cannot delete ${locked.length} locked spec${locked.length !== 1 ? 's' : ''} — unlock first`,
        true
      );
      return;
    }
    if (
      !confirm(
        `Delete ${selected.length} spec${selected.length !== 1 ? 's' : ''}? This cannot be undone.`
      )
    )
      return;
    for (const m of selected) {
      await SpecService.deleteMaster(m.id);
    }
    setSelectedSpecIds(new Set());
    await loadData();
    toast(`Deleted ${selected.length} spec${selected.length !== 1 ? 's' : ''}`);
  }

  async function handleBulkPublish() {
    if (!canPublish) {
      toast('You do not have permission to publish specs', true);
      return;
    }
    if (selectedSpecIds.size === 0) return;
    const selected = masters.filter((m) => selectedSpecIds.has(m.id));
    const publishable = selected.filter((m) => m.status !== 'published' && !m.lockedBy);
    if (publishable.length === 0) {
      toast('No publishable specs selected (already published or locked)', true);
      return;
    }
    if (!confirm(`Publish ${publishable.length} spec${publishable.length !== 1 ? 's' : ''}?`))
      return;
    for (const m of publishable) {
      const updated = {
        ...m,
        status: 'published',
        version: (m.version || 0) + 1,
        lastModified: new Date().toISOString(),
        modifiedBy: userName,
      };
      await SpecService.savePublished(updated);
    }
    setSelectedSpecIds(new Set());
    await loadData();
    toast(`Published ${publishable.length} spec${publishable.length !== 1 ? 's' : ''}`);
  }

  async function handleBulkDiscardDraft() {
    if (!canOverrideDraft) {
      toast('You do not have permission to override drafts', true);
      return;
    }
    if (selectedSpecIds.size === 0) return;
    const selected = masters.filter((m) => selectedSpecIds.has(m.id));
    const locked = selected.filter((m) => m.lockedBy && m.lockedBy !== userName);
    if (locked.length === 0) {
      toast('No locked specs selected (only specs locked by others can be overridden)', true);
      return;
    }
    if (
      !confirm(
        `Discard drafts for ${locked.length} spec${locked.length !== 1 ? 's' : ''}? The locked users' unsaved changes will be lost.`
      )
    )
      return;
    for (const m of locked) {
      await SpecService.forceOverrideDraft(m.id);
    }
    setSelectedSpecIds(new Set());
    await loadData();
    toast(`Discarded ${locked.length} draft${locked.length !== 1 ? 's' : ''}`);
  }

  async function handleQuickPublish(e: React.MouseEvent, m: any) {
    e.stopPropagation();
    if (!canPublish) {
      toast('You do not have permission to publish specs', true);
      return;
    }
    if (m.status === 'published') {
      toast('Already published', true);
      return;
    }
    if (m.lockedBy) {
      toast(`Cannot publish — locked by ${m.lockedBy}`, true);
      return;
    }
    if (!confirm(`Publish "${m.csiNumber} - ${m.title}" as v${(m.version || 0) + 1}?`)) return;
    // Quick publish: bump version, set status to published, save
    const updated = {
      ...m,
      status: 'published',
      version: (m.version || 0) + 1,
      lastModified: new Date().toISOString(),
      modifiedBy: userName,
    };
    await SpecService.savePublished(updated);
    await loadData();
    toast(`Published ${m.csiNumber} v${updated.version}`);
  }

  function toggleSpecSelection(id: string, e?: React.MouseEvent) {
    if (e) e.stopPropagation();
    setSelectedSpecIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelectedSpecIds(new Set(filtered.map((m: any) => m.id)));
  }

  function deselectAll() {
    setSelectedSpecIds(new Set());
  }

  function exitBulkMode() {
    setBulkMode(false);
    setSelectedSpecIds(new Set());
  }

  async function handleOpenReview(draft: SpecMasterDraft) {
    const master = await SpecService.getMaster(draft.blueprintId);
    if (!master) {
      toast('Published spec master not found', true);
      return;
    }
    const diffs = diffBlocks(master.coreContent || [], draft.coreContent || []);
    const summary = diffChangeSummary(master, draft);
    setReviewDraft(draft);
    setReviewDiff(diffs);
    setReviewChangeSummary(summary);
    setReviewNote('');
    setShowReviewModal(true);
  }

  async function handleApprove() {
    if (!reviewDraft) return;
    const result = await SpecService.publishDraft(reviewDraft.id, userName);
    if (result) toast(`Published v${result.version}`);
    setShowReviewModal(false);
    setReviewDraft(null);
    await loadData();
  }

  async function handleReject() {
    if (!reviewDraft) return;
    await SpecService.rejectPublish(reviewDraft.id, userName, reviewNote || undefined);
    toast('Publish request rejected');
    setShowReviewModal(false);
    setReviewDraft(null);
    await loadData();
  }

  // ─── Import Functions ───

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    const dropped = [...e.dataTransfer.files].filter((f) => f.name.endsWith('.docx'));
    if (dropped.length === 0) return;
    // Use the file paths from the drop
    importFromPaths(dropped.map((f) => (f as any).path));
  }

  async function importFromPaths(paths: string[]) {
    setImporting(true);
    let imported = 0;
    for (let i = 0; i < paths.length; i++) {
      const p = paths[i];
      const fname = p.split(/[/\\]/).pop() || p;
      setImportStatus(`Extracting ${i + 1}/${paths.length}: ${fname}`);

      try {
        const result = await window.api.extractDocx(p);
        if (!result.success) {
          toast(`Failed: ${fname} — ${result.error}`, true);
          continue;
        }

        const csi = result.csiNumber;
        const title = result.title;
        const existing = masters.find(
          (m: any) => m.csiNumber.replace(/\s/g, '') === csi.replace(/\s/g, '')
        );
        if (existing) {
          setImportConflict({
            csiNumber: csi,
            title,
            blocks: result.blocks,
            division: result.division,
            existingMaster: existing,
          });
          setConflictNewCsi(csi);
          setConflictNewTitle(title);
          setImporting(false);
          setImportStatus('');
          return;
        }

        await saveImportedSpec(csi, title, result.division, result.blocks);
        imported++;
      } catch (err: any) {
        toast(`Error: ${fname} — ${err.message}`, true);
      }
    }

    setImporting(false);
    setImportStatus('');
    if (imported > 0) {
      await loadData();
      toast(`Imported ${imported} spec${imported !== 1 ? 's' : ''}`);
    }
  }

  async function handleImport() {
    if (!window.api?.openDocxDialog) {
      toast('File dialog not available', true);
      return;
    }
    const paths = await window.api.openDocxDialog();
    if (!paths?.length) return;
    await importFromPaths(paths);
  }

  async function saveImportedSpec(
    csiNumber: string,
    title: string,
    division: string,
    rawBlocks: any[]
  ) {
    const id = SpecService.generateId();
    const coreContent = rawBlocks.map((b: any, i: number) => ({
      id: SpecService.generateId() + '-' + i,
      level: b.level,
      text: b.text,
      bold: b.bold,
      italic: b.italic,
      underline: b.underline,
      blockType: b.blockType,
      tableHtml: b.tableHtml,
      imageData: b.imageData,
      type: 'fixed' as const,
    }));

    const master = {
      id,
      csiNumber,
      title,
      division: division || csiNumber.substring(0, 2),
      status: 'draft' as const,
      version: 1,
      lastModified: new Date().toISOString(),
      modifiedBy: userName,
      coreContent,
      systems: [] as any[],
      variants: [] as any[],
      editHistory: pushEditHistory([], userName, undefined, 'created'),
    };
    await SpecService.savePublished(master as any);
    return id;
  }

  async function handleConflictRename() {
    if (!importConflict) return;
    if (!conflictNewCsi.trim()) {
      toast('Enter a CSI number', true);
      return;
    }
    // Check that the new CSI doesn't also conflict
    const stillConflicts = masters.find(
      (m: any) =>
        m.csiNumber.replace(/\s/g, '') === conflictNewCsi.replace(/\s/g, '') &&
        m.id !== importConflict.existingMaster.id
    );
    if (stillConflicts) {
      toast('That CSI number also exists — choose a different one', true);
      return;
    }

    const newId = await saveImportedSpec(
      conflictNewCsi.trim(),
      conflictNewTitle.trim() || importConflict.title,
      importConflict.division,
      importConflict.blocks
    );
    setImportConflict(null);
    await loadData();
    toast('Imported as new section');
    onOpenEditor(newId);
  }

  async function handleConflictAddVariant() {
    if (!importConflict) return;
    const existing = importConflict.existingMaster;

    // Add all imported blocks as variant blocks on the existing master
    const variantId = 'var-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
    const variantName = importConflict.title || 'Imported Variant';

    // Build variant blocks from imported content
    const variantBlocks = importConflict.blocks.map((b: any, i: number) => ({
      id: SpecService.generateId() + '-v' + i,
      level: b.level,
      text: b.text,
      bold: b.bold,
      italic: b.italic,
      underline: b.underline,
      blockType: b.blockType,
      tableHtml: b.tableHtml,
      imageData: b.imageData,
      type: 'fixed' as const,
      isVariant: true,
      variantId,
    }));

    // Append variant blocks to existing core content
    const updatedContent = [...(existing.coreContent || []), ...variantBlocks];
    const updatedVariants = [...(existing.variants || []), { id: variantId, name: variantName }];

    const updated = {
      ...existing,
      coreContent: updatedContent,
      variants: updatedVariants,
      lastModified: new Date().toISOString(),
      modifiedBy: userName,
      editHistory: pushEditHistory(existing.editHistory || [], userName, undefined, 'edited'),
    };
    await SpecService.savePublished(updated as any);
    setImportConflict(null);
    await loadData();
    toast(`Added as variant on ${existing.csiNumber}`);
    onOpenEditor(existing.id);
  }

  function statusColor(s: string) {
    if (s === 'published' || s === 'active')
      return { bg: 'rgba(var(--okr),0.12)', color: 'var(--ok)', text: 'Published' };
    if (s === 'pending_review') return { bg: WARN_SOFT, color: WARN_TEXT, text: 'Pending review' };
    if (s === 'draft') return { bg: WARN_SOFT, color: WARN_TEXT, text: 'Draft' };
    if (s === 'archived') return { bg: 'var(--sa)', color: 'var(--tm)', text: 'Archived' };
    return { bg: 'var(--sa)', color: 'var(--tm)', text: s || 'Draft' };
  }

  function formatDate(d: string) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const divTree = divisions.filter((d: any) => divCounts[d.code]);

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left panel — Division Tree */}
      <div
        style={{
          width: 240,
          minWidth: 240,
          borderRight: '1px solid var(--bd)',
          background: 'var(--sf)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 'var(--space-3) var(--space-3) var(--space-2)',
            borderBottom: '1px solid var(--bd)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.05em',
            }}
          >
            CSI Divisions
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-1) 0' }}>
          <div
            onClick={() => setSelectedDiv(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: !selectedDiv ? 600 : 400,
              background: !selectedDiv ? 'rgba(var(--acr),0.1)' : 'transparent',
              color: !selectedDiv ? 'var(--ac)' : 'var(--tx)',
              borderRadius: 'var(--radius-sm)',
              margin: '0 var(--space-2)',
              transition: 'all var(--duration-fast) var(--ease-out)',
            }}
          >
            <span style={{ fontSize: 13 }}>All sections</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 10,
                padding: '1px var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--sa)',
                color: 'var(--tm)',
              }}
            >
              {masters.length}
            </span>
          </div>
          {divTree.map((d: any) => {
            const isExp = expandedDivs[d.code];
            const isSel = selectedDiv === d.code;
            const specsInDiv = masters.filter((m: any) => m.division === d.code);
            return (
              <div key={d.code}>
                <div
                  onClick={() => {
                    setSelectedDiv(isSel ? null : d.code);
                    setExpandedDivs((p: any) => ({ ...p, [d.code]: !p[d.code] }));
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: isSel ? 600 : 400,
                    background: isSel ? 'rgba(var(--acr),0.1)' : 'transparent',
                    color: isSel ? 'var(--ac)' : 'var(--tx)',
                    borderRadius: 'var(--radius-sm)',
                    margin: '1px var(--space-2)',
                    transition: 'all var(--duration-fast) var(--ease-out)',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex',
                      color: 'var(--tm)',
                      transform: isExp ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform var(--duration-fast) var(--ease-out)',
                      flexShrink: 0,
                    }}
                  >
                    <ChevronRightIcon />
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--ac)',
                      opacity: 0.7,
                      flexShrink: 0,
                    }}
                  >
                    {d.code}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {d.name}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px var(--space-1)',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--sa)',
                      color: 'var(--tm)',
                      flexShrink: 0,
                    }}
                  >
                    {divCounts[d.code] || 0}
                  </span>
                </div>
                {isExp && (
                  <div>
                    {specsInDiv.map((sp: any) => (
                      <div
                        key={sp.id}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          onOpenEditor(sp.id);
                        }}
                        style={{
                          padding:
                            'var(--space-1) var(--space-3) var(--space-1) calc(var(--space-5) + var(--space-3))',
                          fontSize: 11,
                          cursor: 'pointer',
                          color: 'var(--tm)',
                          transition: 'all var(--duration-fast) var(--ease-out)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLElement).style.color = 'var(--ac)')
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLElement).style.color = 'var(--tm)')
                        }
                      >
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>
                          {sp.csiNumber}
                        </span>{' '}
                        {sp.title}
                        {sp.lockedBy && (
                          <span
                            title={`Locked by ${sp.lockedBy}`}
                            style={{
                              display: 'inline-flex',
                              verticalAlign: 'middle',
                              marginLeft: 'var(--space-1)',
                              color: WARN_TEXT,
                            }}
                          >
                            <LockIcon size={11} />
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pending Reviews section (admin only) */}
        {canReview && pendingPRs.length > 0 && (
          <div
            style={{
              borderTop: '1px solid var(--bd)',
              padding: 'var(--space-2) var(--space-3)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: WARN_TEXT,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
                marginBottom: 'var(--space-2)',
              }}
            >
              Pending reviews ({pendingPRs.length})
            </div>
            {pendingPRs.map((d) => (
              <div
                key={d.id}
                onClick={() => handleOpenReview(d)}
                style={{
                  padding: 'var(--space-2)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: 11,
                  marginBottom: 'var(--space-1)',
                  border: `1px solid ${WARN_BORDER}`,
                  background: WARN_SOFT,
                  transition: 'all var(--duration-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.background = WARN_SOFT_HOVER)
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.background = WARN_SOFT)
                }
              >
                <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--tx)' }}>
                  {d.csiNumber} — {d.title}
                </div>
                <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 2 }}>
                  by {d.editedBy} — {d.publishRequest?.message || 'No message'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-5)',
            borderBottom: '1px solid var(--bd)',
            background: 'var(--sf)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search handled by DataTable — no duplicate search bar here */}
          {!bulkMode && (
            <Button variant="outline" size="sm" onClick={() => setBulkMode(true)}>
              Bulk edit
            </Button>
          )}
          {bulkMode && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={selectedSpecIds.size === filtered.length ? deselectAll : selectAll}
              >
                {selectedSpecIds.size === filtered.length ? 'Deselect all' : 'Select all'}
              </Button>
              {selectedSpecIds.size > 0 && canPublish && (
                <button
                  onClick={handleBulkPublish}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--ok)',
                    background: OK_SOFT,
                    color: 'var(--ok)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    minHeight: 32,
                  }}
                >
                  <PublishIcon size={13} /> Publish {selectedSpecIds.size}
                </button>
              )}
              {selectedSpecIds.size > 0 && canOverrideDraft && (
                <button
                  onClick={handleBulkDiscardDraft}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${WARN_BORDER}`,
                    background: WARN_SOFT,
                    color: WARN_TEXT,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    minHeight: 32,
                  }}
                >
                  <UnlockIcon size={13} /> Discard drafts
                </button>
              )}
              {selectedSpecIds.size > 0 && canDelete && (
                <button
                  onClick={handleMultiDelete}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--dg)',
                    background: DANGER_SOFT,
                    color: 'var(--dg)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    minHeight: 32,
                  }}
                >
                  <TrashIcon size={13} /> Delete {selectedSpecIds.size}
                </button>
              )}
              <Button variant="ghost" size="sm" onClick={exitBulkMode}>
                Done
              </Button>
              {selectedSpecIds.size > 0 && (
                <span style={{ fontSize: 11, color: 'var(--tm)' }}>
                  {selectedSpecIds.size}/{filtered.length}
                </span>
              )}
            </>
          )}
          {/* .docx import needs the local Word engine — desktop only. */}
          {canImport && !isWeb && (
            <Button variant="outline" size="sm" onClick={handleImport} disabled={importing}>
              <DownloadIcon size={14} />
              {importing ? importStatus : 'Import .docx'}
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => setShowNewModal(true)}>
              <PlusIcon size={14} />
              New spec master
            </Button>
          )}
        </div>

        {/* Cards */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={handleDrop}
          style={{ flex: 1, overflow: 'auto', padding: 'var(--space-5)' }}
        >
          {loadError && (
            <div
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                marginBottom: 'var(--space-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--dg)',
                background: DANGER_SOFT,
                fontSize: 13,
              }}
            >
              <span aria-hidden="true" style={{ display: 'inline-flex', color: 'var(--dg)' }}>
                <AlertIcon size={14} />
              </span>
              <span style={{ color: 'var(--tx)' }}>{loadError}</span>
            </div>
          )}
          {loading ? (
            <LibrarySkeleton />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<BookIcon size={28} />}
              title={masters.length === 0 ? 'Spec master library' : 'No sections in this division'}
              subtitle={
                masters.length === 0
                  ? "Create spec masters to build your firm's specification library."
                  : 'Choose another division, or create a new spec master.'
              }
              action={{
                label: 'Create first spec master',
                onClick: () => setShowNewModal(true),
              }}
            />
          ) : (
            (() => {
              // Full SharePoint-list parity for Spec Masters: DataTable with search,
              // sort by any column, group by any column, column resize, sticky header.
              const columns: Column<any>[] = [
                {
                  key: 'csiNumber',
                  label: 'CSI #',
                  width: 110,
                  getValue: (m) => m.csiNumber || '',
                  render: (m) => (
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 600,
                        color: 'var(--ac)',
                      }}
                    >
                      {m.csiNumber || '–'}
                    </span>
                  ),
                },
                {
                  key: 'title',
                  label: 'Title',
                  width: 320,
                  getValue: (m) => m.title || '',
                  render: (m) => (
                    <span
                      style={{
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.title}
                      </span>
                      {m.lockedBy && (
                        <span
                          title={`Locked by ${m.lockedBy}`}
                          style={{ display: 'inline-flex', color: WARN_TEXT }}
                        >
                          <LockIcon size={12} />
                        </span>
                      )}
                      {(m.variants || []).map((v: any) => (
                        <span
                          key={v.id}
                          style={{
                            fontSize: 10,
                            padding: '1px var(--space-1)',
                            borderRadius: 'var(--radius-sm)',
                            background: 'rgba(var(--acr),0.08)',
                            color: 'var(--ac)',
                          }}
                        >
                          {v.name}
                        </span>
                      ))}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  label: 'Status',
                  width: 100,
                  getValue: (m) => m.status || 'draft',
                  render: (m) => {
                    const sc = statusColor(m.status);
                    return (
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px var(--space-2)',
                          borderRadius: 'var(--radius-sm)',
                          background: sc.bg,
                          color: sc.color,
                          fontWeight: 600,
                        }}
                      >
                        {sc.text}
                      </span>
                    );
                  },
                },
                {
                  key: 'variants',
                  label: 'Variants',
                  width: 90,
                  getValue: (m) => (m.variants || []).length,
                },
                {
                  key: 'lockedBy',
                  label: 'Locked by',
                  width: 120,
                  getValue: (m) => m.lockedBy || '',
                  render: (m) => <span style={{ color: 'var(--tm)' }}>{m.lockedBy || '–'}</span>,
                },
                {
                  key: 'version',
                  label: 'Version',
                  width: 80,
                  getValue: (m) => m.version || 0,
                  render: (m) => <span style={{ color: 'var(--tm)' }}>v{m.version || 1}</span>,
                },
                {
                  key: 'lastModified',
                  label: 'Modified',
                  width: 120,
                  getValue: (m) => m.lastModified || '',
                  render: (m) => (
                    <span style={{ color: 'var(--tm)', fontSize: 11 }}>
                      {formatDate(m.lastModified)}
                    </span>
                  ),
                },
              ];
              const divOf = (csi: string) => {
                const s = (csi || '').trim();
                const m = s.match(/^(\d{2})/);
                return m ? `Division ${m[1]}` : 'No Division';
              };
              const groupOptions: GroupOption<any>[] = columns.map((c) => {
                // Group by CSI # = group by Division (first two digits) — full CSI is too granular.
                if (c.key === 'csiNumber') {
                  return {
                    key: 'division',
                    label: 'By Division',
                    getValue: (m: any) => divOf(m.csiNumber || ''),
                  };
                }
                return {
                  key: c.key,
                  label: `By ${c.label}`,
                  getValue: (m: any) => {
                    const v = c.getValue ? c.getValue(m) : (m as any)[c.key];
                    return v == null || v === '' ? `No ${c.label}` : String(v);
                  },
                };
              });
              return (
                <DataTable<any>
                  columns={columns}
                  data={filtered}
                  keyField="id"
                  onRowClick={(m) => onOpenEditor(m.id)}
                  groupOptions={groupOptions}
                  searchPlaceholder="Search specs (any column)…"
                  initialSort={{ key: 'csiNumber', direction: 'asc' }}
                  storageKey="spec-masters-table"
                  actions={(m) => {
                    const isLocked = !!m.lockedBy;
                    const lockedByMe = m.lockedBy === userName;
                    const actionBtnStyle: React.CSSProperties = {
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 32,
                      minHeight: 32,
                      padding: 'var(--space-1)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                    };
                    return (
                      <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                        {isLocked && !lockedByMe && canOverrideDraft && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOverrideDraft(e as any, m);
                            }}
                            title={`Override ${m.lockedBy}'s draft`}
                            aria-label={`Override ${m.lockedBy}'s draft`}
                            style={{ ...actionBtnStyle, color: 'var(--dg)' }}
                          >
                            <UnlockIcon size={14} />
                          </button>
                        )}
                        {m.status !== 'published' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickPublish(e as any, m);
                            }}
                            title="Quick publish"
                            aria-label="Quick publish"
                            style={{ ...actionBtnStyle, color: 'var(--ok)' }}
                          >
                            <PublishIcon size={14} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(e as any, m);
                          }}
                          title="Duplicate"
                          aria-label="Duplicate"
                          style={{ ...actionBtnStyle, color: 'var(--tm)' }}
                        >
                          <CopyIcon size={14} />
                        </button>
                        {canDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(e as any, m);
                            }}
                            title="Delete"
                            aria-label="Delete"
                            style={{ ...actionBtnStyle, color: 'var(--dg)' }}
                          >
                            <TrashIcon size={14} />
                          </button>
                        )}
                      </div>
                    );
                  }}
                />
              );
            })()
          )}
        </div>
      </div>

      {/* New Spec Master Modal */}
      {showNewModal && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowNewModal(false);
          }}
          style={overlayStyle}
        >
          <div style={modalStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 'var(--space-4)' }}>
              New spec master
            </div>
            <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                CSI number
              </div>
              <input
                value={newCsi}
                onChange={(e) => setNewCsi(e.target.value)}
                placeholder="e.g. 09 21 16"
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  fontSize: 13,
                  fontFamily: 'var(--font-mono)',
                }}
                autoFocus
              />
            </label>
            <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Title
              </div>
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Gypsum Board Assemblies"
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  fontSize: 13,
                }}
              />
            </label>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--space-2)',
                marginTop: 'var(--space-5)',
              }}
            >
              <Button variant="ghost" onClick={() => setShowNewModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleNew}>Create</Button>
            </div>
          </div>
        </div>
      )}

      {/* PR Review Modal (admin) */}
      {showReviewModal && reviewDraft && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowReviewModal(false);
          }}
          style={overlayStyle}
        >
          <div style={{ ...modalStyle, width: 640, maxHeight: '85vh' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 'var(--space-1)' }}>
              Review publish request
            </div>
            <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 'var(--space-1)' }}>
              <strong>{reviewDraft.csiNumber}</strong> — {reviewDraft.title}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 'var(--space-1)' }}>
              Submitted by <strong>{reviewDraft.editedBy}</strong> on{' '}
              {formatDate(reviewDraft.publishRequest?.requestedAt || '')}
            </div>
            {reviewDraft.publishRequest?.message && (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--tx)',
                  marginBottom: 'var(--space-3)',
                  fontStyle: 'italic',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--sa)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                "{reviewDraft.publishRequest.message}"
              </div>
            )}

            {/* Diff summary */}
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 'var(--space-1)' }}>
              <strong>Content:</strong> {reviewDiff.filter((d) => d.type === 'added').length} added,{' '}
              {reviewDiff.filter((d) => d.type === 'removed').length} removed,{' '}
              {reviewDiff.filter((d) => d.type === 'modified').length} modified
            </div>
            {reviewChangeSummary &&
              (() => {
                const cs = reviewChangeSummary;
                const parts: string[] = [];
                if (cs.systemsAdded.length) parts.push(`${cs.systemsAdded.length} system(s) added`);
                if (cs.systemsRemoved.length)
                  parts.push(`${cs.systemsRemoved.length} system(s) removed`);
                if (cs.systemsModified.length)
                  parts.push(`${cs.systemsModified.length} system(s) modified`);
                if (cs.variantsAdded.length)
                  parts.push(`${cs.variantsAdded.length} variant(s) added`);
                if (cs.variantsRemoved.length)
                  parts.push(`${cs.variantsRemoved.length} variant(s) removed`);
                if (cs.variantsModified.length)
                  parts.push(`${cs.variantsModified.length} variant(s) modified`);
                if (parts.length === 0) return null;
                return (
                  <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 'var(--space-2)' }}>
                    {parts.join(', ')}
                  </div>
                );
              })()}

            {/* Diff content */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-1)',
                maxHeight: '40vh',
                overflow: 'auto',
                marginBottom: 'var(--space-4)',
              }}
            >
              {reviewDiff
                .filter((d) => d.type !== 'unchanged')
                .map((d, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 'var(--space-2)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 11,
                      border: '1px solid var(--bd)',
                      background:
                        d.type === 'added'
                          ? 'rgba(var(--okr),0.06)'
                          : d.type === 'removed'
                            ? 'color-mix(in srgb, var(--dg) 6%, transparent)'
                            : 'color-mix(in srgb, var(--color-warning) 6%, transparent)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '1px var(--space-1)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-text-inverse)',
                        marginRight: 'var(--space-2)',
                        background:
                          d.type === 'added'
                            ? 'var(--ok)'
                            : d.type === 'removed'
                              ? 'var(--dg)'
                              : 'var(--color-warning)',
                      }}
                    >
                      {d.type === 'added' ? '+' : d.type === 'removed' ? '-' : '~'}
                    </span>
                    {d.type === 'modified' && (
                      <span>
                        <span style={{ color: 'var(--dg)', textDecoration: 'line-through' }}>
                          {d.oldText}
                        </span>{' '}
                        → <span style={{ color: 'var(--ok)' }}>{d.newText}</span>
                      </span>
                    )}
                    {d.type === 'added' && <span style={{ color: 'var(--ok)' }}>{d.newText}</span>}
                    {d.type === 'removed' && (
                      <span style={{ color: 'var(--dg)', textDecoration: 'line-through' }}>
                        {d.oldText}
                      </span>
                    )}
                  </div>
                ))}
              {reviewDiff.filter((d) => d.type !== 'unchanged').length === 0 && (
                <div
                  style={{
                    padding: 'var(--space-3)',
                    textAlign: 'center',
                    color: 'var(--tm)',
                    fontSize: 13,
                  }}
                >
                  No content changes detected.
                </div>
              )}
            </div>

            <label style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Review note (optional)
              </div>
              <input
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="Optional feedback..."
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  fontSize: 13,
                }}
              />
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
              <Button variant="ghost" onClick={() => setShowReviewModal(false)}>
                Cancel
              </Button>
              <button
                onClick={handleReject}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--dg)',
                  background: 'transparent',
                  color: 'var(--dg)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 32,
                }}
              >
                Reject
              </button>
              <button
                onClick={handleApprove}
                style={{
                  padding: 'var(--space-2) var(--space-5)',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: 'var(--ok)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 32,
                }}
              >
                Approve & publish
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Import Conflict Modal */}
      {importConflict && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setImportConflict(null);
          }}
          style={overlayStyle}
        >
          <div style={modalStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 'var(--space-1)' }}>
              Section already exists
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--tm)',
                marginBottom: 'var(--space-4)',
                lineHeight: 1.5,
              }}
            >
              <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--ac)' }}>
                {importConflict.csiNumber}
              </strong>{' '}
              — {importConflict.title}
              <br />
              already matches <strong>{importConflict.existingMaster.csiNumber}</strong> —{' '}
              {importConflict.existingMaster.title}
            </div>

            {/* Option 1: Change number/name */}
            <div
              style={{
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--bd)',
                marginBottom: 'var(--space-3)',
                background: 'var(--sa)',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                Option 1: Import as new section
              </div>
              <div
                style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--tm)',
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    CSI number
                  </div>
                  <input
                    value={conflictNewCsi}
                    onChange={(e) => setConflictNewCsi(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--bd)',
                      background: 'var(--ib)',
                      color: 'var(--tx)',
                      fontSize: 13,
                      fontFamily: 'var(--font-mono)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--tm)',
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    Title
                  </div>
                  <input
                    value={conflictNewTitle}
                    onChange={(e) => setConflictNewTitle(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--bd)',
                      background: 'var(--ib)',
                      color: 'var(--tx)',
                      fontSize: 13,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
              <Button onClick={handleConflictRename} style={{ width: '100%' }}>
                Import as new section
              </Button>
            </div>

            {/* Option 2: Add as variant */}
            <div
              style={{
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--bd)',
                marginBottom: 'var(--space-3)',
                background: 'var(--sa)',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                Option 2: Add as variant
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-2)',
                  lineHeight: 1.4,
                }}
              >
                Import this content as a new variant on the existing section. The imported blocks
                will be added as variant-specific content.
              </div>
              <button
                onClick={handleConflictAddVariant}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${WARN_BORDER}`,
                  background: WARN_SOFT,
                  color: WARN_TEXT,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  minHeight: 32,
                }}
              >
                Add as variant on {importConflict.existingMaster.csiNumber}
              </button>
            </div>

            <Button
              variant="outline"
              onClick={() => setImportConflict(null)}
              style={{ width: '100%' }}
            >
              Skip this file
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,0,0,.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  width: 420,
  border: '1px solid var(--bd)',
  boxShadow: 'var(--shadow-xl)',
  maxHeight: '80vh',
  overflow: 'auto',
};

(window as any).SpecLibrary = SpecLibrary;
export { SpecLibrary };
